import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, X, UserCheck } from "@phosphor-icons/react";

/**
 * Attester search picker.
 *
 * Lets the player search ANY registered user (not just friends) by name or
 * email. Picks one → calls onSelect(userId, userName).
 *
 * Debounces input to avoid hammering the API on every keystroke.
 */
export function AttesterPicker({ open, onClose, onSelect, currentUserId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setQ(""); setResults([]); return; }
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/users/search`, { params: { q: q.trim() } });
        // Defensive: in case API ever returns the caller, filter them out client-side too.
        setResults((r.data || []).filter((u) => u.id !== currentUserId));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Search failed");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, currentUserId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" data-testid="attester-picker-modal">
      <div className="w-full sm:max-w-md card-default p-5 sm:rounded-2xl rounded-t-2xl">
        <div className="flex items-center justify-between gap-3">
          <div>
            <span className="label-eyebrow">Add attester</span>
            <h3 className="font-heading font-bold text-xl mt-1">Pick a marker</h3>
          </div>
          <button onClick={onClose} data-testid="attester-picker-close" className="text-[#0F2537]/60 hover:text-[#0F2537] p-1"><X size={20} /></button>
        </div>
        <p className="text-xs text-[#0F2537]/60 mt-2">Search any registered Bula Golf user by name or email. They'll get a notification asking to attest your scorecard.</p>

        <div className="mt-4 relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F2537]/40" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type at least 2 letters…"
            data-testid="attester-search-input"
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]"
          />
        </div>

        <div className="mt-4 max-h-72 overflow-y-auto">
          {loading && <div className="text-sm text-[#0F2537]/50 py-3 text-center">Searching…</div>}
          {!loading && q.trim().length >= 2 && results.length === 0 && (
            <div className="text-sm text-[#0F2537]/50 py-3 text-center">No matches.</div>
          )}
          {!loading && q.trim().length < 2 && (
            <div className="text-sm text-[#0F2537]/50 py-3 text-center">Start typing to find a golfer.</div>
          )}
          <div className="space-y-1.5">
            {results.map((u) => (
              <button
                key={u.id}
                onClick={() => onSelect(u.id, u.name)}
                data-testid={`attester-pick-${u.id}`}
                className="w-full text-left p-3 rounded-xl border border-[#E2E0D8] hover:border-[#0F2537] flex items-center gap-3 transition"
              >
                <div className="w-9 h-9 rounded-full bg-[#0F2537] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {(u.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">{u.name}</div>
                  <div className="text-[11px] text-[#0F2537]/60 truncate">
                    {u.email}{u.handicap_index != null ? ` · HCP ${u.handicap_index.toFixed(1)}` : ""}
                  </div>
                </div>
                <UserCheck size={18} className="text-[#E05D3A] shrink-0" weight="bold" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
